import { timeoutLogger } from './timeoutLogger';

export interface RequestCheckpoint {
  name: string;
  timestamp: number;
  duration: number;
  metadata?: Record<string, any>;
}

export class RequestTracer {
  private requestId: string;
  private operationName: string;
  private startTime: number;
  private checkpoints: RequestCheckpoint[] = [];
  private timeoutMs?: number;
  private warningThreshold: number;

  constructor(operationName: string, timeoutMs?: number, warningThreshold = 0.75) {
    this.requestId = timeoutLogger.generateRequestId();
    this.operationName = operationName;
    this.startTime = Date.now();
    this.timeoutMs = timeoutMs;
    this.warningThreshold = warningThreshold;

    timeoutLogger.logOperationStart(this.requestId, operationName, {
      timeoutMs,
      requestId: this.requestId,
    });
  }

  getRequestId(): string {
    return this.requestId;
  }

  addCheckpoint(name: string, metadata?: Record<string, any>): void {
    const now = Date.now();
    const duration = now - this.startTime;

    this.checkpoints.push({
      name,
      timestamp: now,
      duration,
      metadata,
    });

    timeoutLogger.logOperationProgress(this.requestId, this.operationName, name, {
      duration: `${duration}ms`,
      ...metadata,
    });

    if (this.timeoutMs) {
      const percentElapsed = duration / this.timeoutMs;
      if (percentElapsed >= this.warningThreshold && percentElapsed < 1) {
        timeoutLogger.logOperationWarning(
          this.requestId,
          this.operationName,
          `Approaching timeout (${name})`,
          Math.round(percentElapsed * 100)
        );
      }
    }
  }

  complete(metadata?: Record<string, any>): void {
    const duration = Date.now() - this.startTime;
    timeoutLogger.logOperationComplete(this.requestId, this.operationName, duration, {
      checkpoints: this.checkpoints.length,
      ...metadata,
    });
  }

  timeout(actualDuration: number, metadata?: Record<string, any>): void {
    const now = Date.now();
    const duration = now - this.startTime;

    console.log(`⏱️ [TIMEOUT-TRACE] ${this.requestId} - ${this.operationName} timed out after ${actualDuration}ms`);
    console.log(`⏱️ [TIMEOUT-TRACE] Checkpoints reached before timeout:`);

    this.checkpoints.forEach((checkpoint, index) => {
      console.log(
        `⏱️ [TIMEOUT-TRACE]   ${index + 1}. ${checkpoint.name} (${checkpoint.duration}ms)`,
        checkpoint.metadata || ''
      );
    });

    if (this.checkpoints.length === 0) {
      console.log(`⏱️ [TIMEOUT-TRACE]   No checkpoints reached - operation likely stalled at start`);
    }
  }

  getCheckpoints(): RequestCheckpoint[] {
    return [...this.checkpoints];
  }

  getDuration(): number {
    return Date.now() - this.startTime;
  }
}

export function createTracer(operationName: string, timeoutMs?: number): RequestTracer {
  return new RequestTracer(operationName, timeoutMs);
}
